import { useEffect, useImperativeHandle, useRef, forwardRef, useCallback, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";

/**
 * Continuous film player with a single <video> element and robust auto-advance.
 */
export const ContinuousFilmPlayer = forwardRef(function ContinuousFilmPlayer(
  { playlist, activeIndex, onIndexChange, autoplay = true, flip = false }, ref
) {
  const videoRef = useRef(null);
  const playlistRef = useRef(playlist);
  const indexRef = useRef(activeIndex);
  const onIndexChangeRef = useRef(onIndexChange);
  const autoplayRef = useRef(autoplay);
  const advancingFromRef = useRef(null);
  const currentSrcRef = useRef(null);
  const wrapperRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useImperativeHandle(ref, () => ({
    seekTo(seconds) {
      const el = videoRef.current;
      if (!el) return;
      const doSeek = () => {
        el.currentTime = seconds;
        if (autoplayRef.current) el.play().catch(() => {});
      };
      if (el.readyState >= 1) doSeek();
      else el.addEventListener("loadedmetadata", doSeek, { once: true });
    },
  }), []);

  useEffect(() => { playlistRef.current = playlist; }, [playlist]);
  useEffect(() => { onIndexChangeRef.current = onIndexChange; }, [onIndexChange]);
  useEffect(() => { autoplayRef.current = autoplay; }, [autoplay]);
  useEffect(() => {
    indexRef.current = activeIndex;
    if (advancingFromRef.current !== null && activeIndex > advancingFromRef.current) {
      advancingFromRef.current = null;
    }
  }, [activeIndex]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const list = playlistRef.current;
    const idx = Math.max(0, Math.min(list.length - 1, activeIndex));
    const url = list[idx]?.url ?? null;
    if (!url) return;
    if (currentSrcRef.current !== url) {
      currentSrcRef.current = url;
      el.src = url;
      try { el.load(); } catch (e) { /* ignore */ }
    }
    if (autoplayRef.current) {
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          const onReady = () => { el.removeEventListener("canplay", onReady); el.play().catch(() => {}); };
          el.addEventListener("canplay", onReady, { once: true });
        });
      }
    }
  }, [activeIndex, playlist]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const advance = () => {
      const list = playlistRef.current;
      const fromIdx = indexRef.current;
      if (advancingFromRef.current === fromIdx) return;
      if (fromIdx >= list.length - 1) return;
      advancingFromRef.current = fromIdx;
      try { el.pause(); } catch (e) { /* ignore */ }
      onIndexChangeRef.current(fromIdx + 1);
    };
    const handleEnded = () => advance();
    const handleTimeUpdate = () => {
      if (!isFinite(el.duration) || el.duration <= 0) return;
      if (el.paused) return;
      if (el.currentTime >= el.duration - 0.4) advance();
    };
    const handleError = () => advance();
    el.addEventListener("ended", handleEnded);
    el.addEventListener("timeupdate", handleTimeUpdate);
    el.addEventListener("error", handleError);
    return () => {
      el.removeEventListener("ended", handleEnded);
      el.removeEventListener("timeupdate", handleTimeUpdate);
      el.removeEventListener("error", handleError);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else wrapper.requestFullscreen().catch(() => {});
  }, []);

  useEffect(() => {
    const handle = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handle);
    return () => document.removeEventListener("fullscreenchange", handle);
  }, []);

  return (
    <div ref={wrapperRef} className="w-full h-full bg-black relative group" onDoubleClick={toggleFullscreen}>
      <video ref={videoRef} controls preload="auto" playsInline autoPlay={autoplay}
        className="w-full h-full object-contain bg-black"
        style={flip ? { transform: "rotate(180deg)" } : undefined} data-testid="film-video" />
      <button onClick={toggleFullscreen}
        className="absolute top-2 right-2 z-10 bg-black/60 hover:bg-black/80 text-white rounded p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
        title={isFullscreen ? "Ieșire fullscreen" : "Fullscreen"}>
        {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
      </button>
    </div>
  );
});
